import { EB_IMAGE_WORDMARK } from '@/cmsBackend/constants';
import { Env } from '@/common/types';

import dynamic from 'next/dynamic';
import Head from 'next/head';
import React from 'react';
import { FallbackUI } from '../FallbackUI/FallbackUI';

const NavbarComponent = dynamic(
  () =>
    import(/* webpackChunkName: "navbar_component" */ '@/components/global').then(
      (mod) => mod.Navbar,
    ),
  { ssr: true, loading: () => null },
);

const FooterComponent = dynamic(
  () =>
    import(/* webpackChunkName: "footer_component" */ '@/components/global').then(
      (mod) => mod.Footer,
    ),
  { ssr: true },
);

interface FallbackPageProps {
  children?: React.ReactNode;
  statusCode: number;
  env: Env;
}

/**
 * This is a Fallback page to handle 4xx and 5xx errors
 * It adds no-index meta tags and renders fallback UI
 */
export const FallbackPage: React.FC<FallbackPageProps> = ({ children, statusCode, env }) => {
  const fallback = children ? children : <FallbackUI homeUrl={`https://${env.ebHost}/`} />;

  const ogTags =
    statusCode === 404
      ? {
          title: 'Page Not Found',
          description: 'Whoops, the page you are looking for was not found.',
        }
      : {
          title: 'Something went wrong',
          description: 'Oops! Something went wrong, please try again later',
        };

  return (
    <>
      <Head>
        <title>{`Eventbrite | ${statusCode}`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={ogTags.title} />
        <meta property="og:description" content={ogTags.description} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={EB_IMAGE_WORDMARK} />
      </Head>
      <NavbarComponent />
      <main>{fallback}</main>
      <FooterComponent env={env} />
    </>
  );
};
